import { getParameters } from './analytics';

/**
 * Copies text using Clipboard API.
 *
 * @param {string} text Text to copy.
 * @return {Promise<void>}
 */
const copyTextToClipboard = async ( text ) => {

	if ( ! navigator.clipboard || ! window.isSecureContext ) {

		throw new Error( 'Copy command failed.' );

	}

	await navigator.clipboard.writeText( text );

};

/**
 * Detects if native share should be used.
 *
 * @return {boolean}
 */
const shouldUseNativeShare = () => {

	return Boolean( navigator.share ) && window.matchMedia( '(max-width: 991px)' ).matches;

};

/**
 * Runs native share API for current article.
 *
 * @param {string} title Share title.
 * @param {string} url Share URL.
 * @return {Promise<void>}
 */
const nativeShare = async ( title, url ) => {

	if ( ! title || ! url || ! navigator.share ) {

		return;

	}

	await navigator.share( { title, url } );

};

/**
 * Tracks the selected share option; the share itself may still be cancelled.
 *
 * @param {HTMLElement} shareContainer Share container holding content parameters.
 * @param {string} method Share method.
 * @return {void}
 */
const trackShare = ( shareContainer, method ) => {

	window.MNLAnalytics?.track( 'share', { ...getParameters( shareContainer ), method } );

};

/**
 * Initializes copy-link share button behavior for single news/event pages.
 *
 * @return {void}
 */
const initSingleCopyShare = () => {

	const shareContainers = document.querySelectorAll( '.single-news__share[data-share-url], .single-event__share[data-share-url]' );
	const feedbackTimers = new WeakMap();

	if ( ! shareContainers.length ) {

		return;

	}

	shareContainers.forEach( ( shareContainer ) => {

		const copyButton = shareContainer.querySelector( '[class*="__share-link--copy"][data-copy-url]' );
		const facebookButton = shareContainer.querySelector( '[class*="__share-link--facebook"]' );
		const statusElement = shareContainer.querySelector( '[class*="__share-status"]' );
		const feedbackElement = shareContainer.querySelector( '[class*="__share-feedback"]' );
		const shareUrl = shareContainer.getAttribute( 'data-share-url' ) || '';
		const shareTitle = shareContainer.getAttribute( 'data-share-title' ) || '';
		const nativeShareErrorText = shareContainer.getAttribute( 'data-native-share-error-text' ) || '';

		/**
		 * Shows share action feedback for screen readers and visual users.
		 *
		 * @param {string} message Feedback text.
		 * @param {boolean} isError Whether feedback is an error state.
		 * @param {Element|null} sourceButton Source action button.
		 * @return {void}
		 */
		const setFeedback = ( message, isError = false, sourceButton = null ) => {

			if ( statusElement ) {

				statusElement.textContent = message;

			}

			if ( ! feedbackElement ) {

				return;

			}

			feedbackElement.textContent = message;
			feedbackElement.classList.add( 'is-visible' );
			feedbackElement.classList.toggle( 'is-error', isError );

			if ( sourceButton && feedbackTimers.has( sourceButton ) ) {

				window.clearTimeout( feedbackTimers.get( sourceButton ) );

			}

			const timerId = window.setTimeout( () => {

				feedbackElement.classList.remove( 'is-visible', 'is-error' );

			}, 2000 );

			if ( sourceButton ) {

				feedbackTimers.set( sourceButton, timerId );

			}

		};

		if ( facebookButton ) {

			facebookButton.addEventListener( 'click', async ( event ) => {

				const useNativeShare = shouldUseNativeShare();

				trackShare( shareContainer, useNativeShare ? 'native' : 'facebook' );

				if ( ! useNativeShare ) {

					return;

				}

				event.preventDefault();

				try {

					await nativeShare( shareTitle, shareUrl );

				} catch ( error ) {

					if ( error && error.name === 'AbortError' ) {

						return;

					}

					if ( nativeShareErrorText ) {

						setFeedback( nativeShareErrorText, true, facebookButton );

					}

				}

			} );

		}

		if ( ! copyButton ) {

			return;

		}

		copyButton.addEventListener( 'click', async () => {

			const copyUrl = copyButton.getAttribute( 'data-copy-url' ) || '';
			const successText = copyButton.getAttribute( 'data-copy-success-text' ) || '';
			const errorText = copyButton.getAttribute( 'data-copy-error-text' ) || '';

			if ( ! copyUrl ) {

				return;

			}

			trackShare( shareContainer, 'copy_link' );

			try {

				await copyTextToClipboard( copyUrl );
				copyButton.classList.remove( 'is-copy-error' );
				copyButton.classList.add( 'is-copy-success' );
				setFeedback( successText, false, copyButton );
				window.setTimeout( () => {

					copyButton.classList.remove( 'is-copy-success' );

				}, 350 );

			} catch ( error ) {

				copyButton.classList.remove( 'is-copy-success' );
				copyButton.classList.add( 'is-copy-error' );
				setFeedback( errorText, true, copyButton );
				window.setTimeout( () => {

					copyButton.classList.remove( 'is-copy-error' );

				}, 350 );

			}

		} );

	} );

};

initSingleCopyShare();
